import { useState } from 'react';
import {
  Plus,
  Search,
  BookOpen,
  ArrowLeft,
  X,
  GripVertical,
  ChevronRight,
  Trash2,
  Play,
  CheckCircle2,
  SkipForward,
  Clock,
} from 'lucide-react';
import {
  usePlaybooks,
  useCreatePlaybook,
  usePlaybookInstances,
  useStartPlaybook,
  useUpdatePlaybookStep,
  type Playbook,
  type PlaybookStep,
  type PlaybookInstance,
} from '../hooks/usePlaybooks';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface PlaybooksViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const categoryOptions = [
  'Onboarding',
  'Renewal',
  'Claims',
  'New Business',
  'Compliance',
  'General',
];

type ViewMode = 'list' | 'form' | 'instances';

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// --- Playbook Form ---

interface PlaybookFormProps {
  initial?: Playbook;
  onSave: (data: Partial<Playbook>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function PlaybookForm({ initial, onSave, onCancel, isSaving }: PlaybookFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'General');
  const [steps, setSteps] = useState<PlaybookStep[]>(initial?.steps ?? []);

  function addStep() {
    const newStep: PlaybookStep = {
      id: generateId(),
      title: '',
      description: '',
      assigneeRole: '',
      estimatedMinutes: 15,
      required: true,
      order: steps.length,
    };
    setSteps([...steps, newStep]);
  }

  function removeStep(index: number) {
    setSteps(steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })));
  }

  function updateStep(index: number, updates: Partial<PlaybookStep>) {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  }

  function moveStep(from: number, direction: 'up' | 'down') {
    const to = direction === 'up' ? from - 1 : from + 1;
    if (to < 0 || to >= steps.length) return;
    const updated = [...steps];
    [updated[from], updated[to]] = [updated[to], updated[from]];
    setSteps(updated.map((s, i) => ({ ...s, order: i })));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, description, category, steps });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Basic Information</h3>
        <div>
          <label className="label">Name</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Playbook name"
            required
          />
        </div>
        <div>
          <label className="label">Description</label>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this playbook for?"
            rows={2}
          />
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Steps */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            Steps <span className="font-normal text-slate-400">({steps.length})</span>
          </h3>
          <button type="button" onClick={addStep} className="btn-secondary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Step
          </button>
        </div>

        {steps.length === 0 && (
          <p className="text-sm text-slate-400">
            Add steps to define the process for this playbook.
          </p>
        )}

        {steps.map((step, index) => (
          <div
            key={step.id}
            className="rounded-lg border border-surface-200 bg-surface-50 p-3 space-y-3"
          >
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-slate-400" />
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-medium text-brand-700">
                {index + 1}
              </span>
              <input
                type="text"
                className="input flex-1"
                placeholder="Step title"
                value={step.title}
                onChange={(e) => updateStep(index, { title: e.target.value })}
                required
              />
              <button
                type="button"
                onClick={() => moveStep(index, 'up')}
                disabled={index === 0}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4 -rotate-90" />
              </button>
              <button
                type="button"
                onClick={() => moveStep(index, 'down')}
                disabled={index === steps.length - 1}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4 rotate-90" />
              </button>
              <button
                type="button"
                onClick={() => removeStep(index)}
                className="p-1 text-slate-400 hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div>
              <textarea
                className="input"
                placeholder="Step description (optional)"
                value={step.description ?? ''}
                onChange={(e) => updateStep(index, { description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="label">Assignee Role</label>
                <input
                  type="text"
                  className="input"
                  placeholder="e.g. CSR, Agent, Manager"
                  value={step.assigneeRole ?? ''}
                  onChange={(e) => updateStep(index, { assigneeRole: e.target.value })}
                />
              </div>
              <div className="w-32">
                <label className="label">Est. Minutes</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={step.estimatedMinutes ?? 15}
                  onChange={(e) =>
                    updateStep(index, { estimatedMinutes: parseInt(e.target.value) || 15 })
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input
                  type="checkbox"
                  id={`required-${step.id}`}
                  checked={step.required}
                  onChange={(e) => updateStep(index, { required: e.target.checked })}
                  className="h-4 w-4 rounded border-surface-300 text-brand-600"
                />
                <label htmlFor={`required-${step.id}`} className="text-sm text-slate-600">
                  Required
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSaving || !name.trim()}>
          {isSaving ? 'Saving...' : initial ? 'Update Playbook' : 'Create Playbook'}
        </button>
      </div>
    </form>
  );
}

// --- Active Instances Panel ---

function ActiveInstancesPanel({ onBack }: { onBack: () => void }) {
  const { data, isLoading } = usePlaybookInstances();
  const updateStep = useUpdatePlaybookStep();
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});

  function handleComplete(instanceId: string, stepId: string) {
    updateStep.mutate({
      instanceId,
      stepId,
      data: { status: 'completed', notes: notesMap[`${instanceId}-${stepId}`] },
    });
  }

  function handleSkip(instanceId: string, stepId: string) {
    updateStep.mutate({
      instanceId,
      stepId,
      data: { status: 'skipped', notes: notesMap[`${instanceId}-${stepId}`] },
    });
  }

  const instances = data?.instances ?? [];

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to playbooks
      </button>

      <h2 className="text-lg font-semibold text-slate-900 mb-4">Active Playbook Instances</h2>

      {isLoading ? (
        <div className="card divide-y divide-surface-100">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-4">
              <div className="h-6 w-48 animate-pulse rounded bg-surface-200" />
            </div>
          ))}
        </div>
      ) : instances.length === 0 ? (
        <EmptyState
          icon={Play}
          title="No active instances"
          description="Start a playbook to track progress on a process."
        />
      ) : (
        <div className="space-y-4">
          {instances.map((instance) => {
            const completedCount = instance.steps.filter(
              (s) => s.status === 'completed' || s.status === 'skipped',
            ).length;
            const totalSteps = instance.steps.length;

            return (
              <div key={instance.id} className="card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      {instance.playbookName}
                    </h3>
                    {instance.entityName && (
                      <span className="text-xs text-slate-400">{instance.entityName}</span>
                    )}
                  </div>
                  <Badge color={instance.status === 'active' ? 'blue' : 'green'}>
                    {completedCount}/{totalSteps} steps
                  </Badge>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 w-full rounded-full bg-surface-200">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-all"
                    style={{ width: `${totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0}%` }}
                  />
                </div>

                {/* Steps */}
                <div className="divide-y divide-surface-100">
                  {instance.steps.map((step, stepIdx) => {
                    const key = `${instance.id}-${step.stepId}`;
                    return (
                      <div key={step.stepId} className="flex items-start gap-3 py-2">
                        {step.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                        ) : step.status === 'skipped' ? (
                          <SkipForward className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-surface-300 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span
                            className={cn(
                              'text-sm',
                              step.status === 'completed'
                                ? 'text-slate-400 line-through'
                                : step.status === 'skipped'
                                  ? 'text-slate-400'
                                  : 'text-slate-900',
                            )}
                          >
                            Step {stepIdx + 1}
                          </span>
                          {step.status === 'pending' && (
                            <div className="mt-1 flex items-center gap-2">
                              <input
                                type="text"
                                className="input py-1 text-xs flex-1"
                                placeholder="Notes (optional)"
                                value={notesMap[key] ?? ''}
                                onChange={(e) =>
                                  setNotesMap((m) => ({ ...m, [key]: e.target.value }))
                                }
                              />
                              <button
                                onClick={() => handleComplete(instance.id, step.stepId)}
                                className="btn-primary btn-sm py-1"
                                disabled={updateStep.isPending}
                              >
                                Complete
                              </button>
                              <button
                                onClick={() => handleSkip(instance.id, step.stepId)}
                                className="btn-secondary btn-sm py-1"
                                disabled={updateStep.isPending}
                              >
                                Skip
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Main View ---

export function PlaybooksView({ onNavigate }: PlaybooksViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [searchText, setSearchText] = useState('');
  const [editingPlaybook, setEditingPlaybook] = useState<Playbook | undefined>();

  const { data, isLoading, error } = usePlaybooks();
  const createPlaybook = useCreatePlaybook();
  const startPlaybook = useStartPlaybook();

  function handleCreate() {
    setEditingPlaybook(undefined);
    setMode('form');
  }

  function handleEdit(playbook: Playbook) {
    setEditingPlaybook(playbook);
    setMode('form');
  }

  function handleSave(formData: Partial<Playbook>) {
    createPlaybook.mutate(formData, { onSuccess: () => setMode('list') });
  }

  function handleStart(playbookId: string) {
    startPlaybook.mutate({ playbookId });
  }

  const playbooks = data?.playbooks ?? [];
  const filtered = searchText
    ? playbooks.filter(
        (p) =>
          p.name.toLowerCase().includes(searchText.toLowerCase()) ||
          p.category.toLowerCase().includes(searchText.toLowerCase()),
      )
    : playbooks;

  // --- Instances Mode ---
  if (mode === 'instances') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Playbooks</h1>
            <p className="mt-0.5 text-sm text-slate-500">Active instances</p>
          </div>
        </div>
        <ActiveInstancesPanel onBack={() => setMode('list')} />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingPlaybook ? 'Edit Playbook' : 'New Playbook'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingPlaybook
                ? 'Update the playbook configuration'
                : 'Define a new process playbook'}
            </p>
          </div>
        </div>
        <PlaybookForm
          initial={editingPlaybook}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createPlaybook.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Playbooks</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} playbook${data.total !== 1 ? 's' : ''}` : 'Process templates'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={() => setMode('instances')}>
            <Play className="h-4 w-4" />
            Active Instances
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            New Playbook
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <form onSubmit={(e) => e.preventDefault()} className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search playbooks..."
            className="input pl-9"
          />
        </form>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load playbooks. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            {filtered.map((playbook) => {
              const totalMinutes = playbook.steps.reduce(
                (sum, s) => sum + (s.estimatedMinutes ?? 0),
                0,
              );
              return (
                <div
                  key={playbook.id}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50 transition-colors"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-600">
                    <BookOpen className="h-5 w-5" />
                  </div>

                  <div
                    className="min-w-0 flex-1 cursor-pointer"
                    onClick={() => handleEdit(playbook)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{playbook.name}</span>
                      <Badge color="blue" size="sm">{playbook.category}</Badge>
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                      <span>{playbook.steps.length} step{playbook.steps.length !== 1 ? 's' : ''}</span>
                      {totalMinutes > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {totalMinutes >= 60
                            ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
                            : `${totalMinutes}m`}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleStart(playbook.id)}
                    className="btn-secondary btn-sm"
                    title="Start playbook"
                  >
                    <Play className="h-3.5 w-3.5" />
                    Start
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={BookOpen}
            title="No playbooks"
            description="Create your first playbook to standardize your processes."
            actionLabel="Create Playbook"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
